import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Message, WebSocketMessage } from '../types/types';

const WEBSOCKET_URL = 'ws://localhost:8000/ws';
const RECONNECT_DELAY = 3000;

const welcomeMessages = [
  "Hello! I'm LedgerX365 Assistant. How can I help you with your accounting needs today?",
  "Hi there! Welcome to LedgerX365. I'm here to assist you with any questions about your financial management.",
  "Greetings! I'm your LedgerX365 AI assistant. What would you like to know about your accounting system?",
  "Hello! I'm here to help you navigate LedgerX365. What can I assist you with today?",
  "Welcome to LedgerX365! I'm your AI assistant, ready to help with any accounting or financial questions you have.",
];

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const messageHandlerRef = useRef<((data: WebSocketMessage) => void) | null>(null);

  const connect = useCallback(() => {
    try {
      const ws = new WebSocket(WEBSOCKET_URL);
      
      ws.onopen = () => {
        console.log('Connected to WebSocket');
        setIsConnected(true);
        
        // Send connect message with user_id if available
        // Note: The backend will handle user_id generation if not provided
        try {
          const storedUserId = localStorage.getItem('ledgerx_user_id');
          if (storedUserId) {
            ws.send(JSON.stringify({
              type: 'connect',
              user_id: storedUserId
            }));
          }
        } catch (e) {
          console.warn('Could not send connect message:', e);
        }
        
        // Ensure message handler is (re)bound on connect
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (messageHandlerRef.current) {
              messageHandlerRef.current(data);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
      };

      ws.onclose = () => {
        console.log('WebSocket connection closed');
        setIsConnected(false);
        // Attempt to reconnect after delay
        setTimeout(connect, RECONNECT_DELAY);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setIsConnected(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((message: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        message: message,
        timestamp: new Date().toISOString()
      }));
      return true;
    }
    return false;
  }, []);

  const onMessage = useCallback((callback: (data: WebSocketMessage) => void) => {
    // Save callback; bind to existing socket if available
    messageHandlerRef.current = callback;
    if (wsRef.current) {
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (messageHandlerRef.current) {
            messageHandlerRef.current(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
    }
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    isConnected,
    sendMessage,
    onMessage,
    connect,
    disconnect,
  };
};

export const useChatBot = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  const { isConnected, sendMessage, onMessage } = useWebSocket();

  const addMessage = useCallback((message: Message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const handleSendMessage = useCallback(() => {
    if (!inputMessage?.trim() || !isConnected) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    addMessage(userMessage);
    setInputMessage('');
    setIsLoading(true);

    const sent = sendMessage(inputMessage.trim());
    if (!sent) {
      // Fallback if WebSocket is not connected
      setTimeout(() => {
        const fallbackMessage: Message = {
          id: Date.now().toString(),
          text: 'I\'m currently offline. Please check your connection and try again.',
          sender: 'bot',
          timestamp: new Date(),
        };
        addMessage(fallbackMessage);
        setIsLoading(false);
      }, 1000);
    }
  }, [inputMessage, isConnected, sendMessage, addMessage]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  useEffect(() => {
    onMessage((data) => {
      // Handle different message types from backend
      if (data.type === 'client_id') {
        // Client ID assignment - no need to add message
        return;
      } else if (data.type === 'connected') {
        // Backend connected - store user_id if provided
        if (data.user_id) {
          try {
            localStorage.setItem('ledgerx_user_id', data.user_id);
          } catch (e) {
            console.warn('Could not store user_id:', e);
          }
        }
        
        // Load recent messages if any
        if (data.recent_messages && data.recent_messages.length > 0) {
          // Backend already has history, load those messages
          setMessages(data.recent_messages.map((msg: any) => ({
            id: Date.now().toString() + Math.random(),
            text: msg.content || msg.message || '',
            sender: msg.role === 'assistant' ? 'bot' : 'user',
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          })));
          setHasInitialized(true);
        } else {
          // No history - wait for backend welcome message
          setHasInitialized(true);
        }
      } else if (data.type === 'message') {
        // Clean message text - remove "Bot:" prefix if present
        let messageText = data.message || 'Sorry, I received an empty response.';
        messageText = messageText.replace(/^Bot:\s*/i, '').trim();
        
        const botMessage: Message = {
          id: Date.now().toString(),
          text: messageText,
          sender: 'bot',
          timestamp: new Date(data.timestamp || Date.now()),
        };
        addMessage(botMessage);
        setIsLoading(false);
      }
    });
  }, [onMessage, addMessage]);

  return {
    messages,
    inputMessage,
    setInputMessage,
    isLoading,
    isConnected,
    handleSendMessage,
    handleKeyPress,
  };
};
